import {
    baseActions,
    BaseActionsProps,
    baseMutations,
    baseState,
    BaseState,
    fetchItems,
    FilterParams,
} from "@/store/modules/helpers/baseState";
import { subsetGetter, SubsetGetterProps, Subsets, subsetSetter } from "@/store/modules/helpers/baseSubset";
import { resetDelayedResetTimeout } from "@/store/modules/helpers/delayedReset";
import cloneDeep from "lodash/cloneDeep";

export type Vardform = {
    id: number;
    namn: string;
    typ: string;
};

export type VardformDisplay = {
    value: Vardform["id"];
    label: string;
    typ: Vardform["typ"];
};

type State = BaseState & {
    vardformer: Map<Vardform["id"], Vardform>;
};

export const convertVardformData: (args: { vardform?: Vardform }) => VardformDisplay | null = ({ vardform }) => {
    if (!vardform) {
        return null;
    }

    return {
        value: vardform.id,
        label: vardform.namn,
        typ: vardform.typ,
    };
};

/**
 * Varje default state ska innehålla BaseState + det som är specifikt för den här modulen.
 * Se helpers/Basestate
 */
const getDefaultState: () => State = () => {
    return {
        vardformer: new Map(),
        ...cloneDeep(baseState),
    };
};

const moduleName = "vardformer";

export default {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        vardform: (state: State) => [...state.vardformer.values()],
        vardformSubset: (state: State) => (props: SubsetGetterProps<State> & { displayMode?: boolean }) => {
            const result = subsetGetter<State>({
                ...props,
                state,
                limit: 0,
                collectionName: moduleName,
            });

            if (props.displayMode && Array.isArray(result)) {
                return result.map((singleResult) => convertVardformData({ vardform: singleResult }));
            }

            return result;
        },
        vardformById:
            (state: State) =>
            ({ id }: { id?: number }) => {
                if (!id) {
                    return null;
                }

                return state.vardformer.get(id);
            },
    },
    /**
     * Mutations ska alltid innehålla baseMutations + det som är specifikt för modulen
     */
    mutations: {
        ...baseMutations({
            reset: (state: State) => {
                Object.assign(state, getDefaultState());

                resetDelayedResetTimeout({ moduleName });
            },
        }),
        addVardformer: (
            state: State,
            {
                offset,
                resetSubset,
                subsetName = "default",
                vardformer = [],
            }: {
                resetSubset?: boolean;
                offset: number;
                subsetName: keyof Subsets;
                vardformer: Vardform[];
            }
        ) =>
            subsetSetter<State, Vardform>({
                offset,
                subsetName,
                state,
                resetSubset,
                collectionName: moduleName,
                items: vardformer,
            }),
    },
    actions: {
        ...baseActions({
            moduleName,
        }),
        fetchVardformer: async (
            { commit, state }: BaseActionsProps<State>,
            {
                displayMode,
                filterParams = {},
                config = {},
            }: {
                offset?: number;
                filterParams?: FilterParams;
                config?: {
                    tabbnamn?: string;
                };
                displayMode?: boolean;
            }
        ) => {
            try {
                const {
                    subsetName,
                    results,
                    total,
                    nextPage,
                    offset: resultOffset,
                } = await fetchItems<State, Vardform>({
                    filterParams,
                    state,
                    config,
                    page: 1,
                    limit: 0,
                    collectionName: moduleName,
                    apiUrl: "operation/kodverk/vardformer/",
                });

                if (!results) {
                    return {
                        subsetName,
                        total,
                        nextPage,
                    };
                }

                commit("addVardformer", {
                    subsetName,
                    offset: resultOffset,
                    vardformer: results,
                });

                return {
                    total,
                    nextPage,
                    subsetName,
                    results: displayMode
                        ? results.map((singleResult) => convertVardformData({ vardform: singleResult }))
                        : results,
                };
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
    },
};
